import { useMutation } from '@apollo/client/react';
import { useCallback } from 'react';
import { useViewer } from '@/features/session';
import { firstUserErrorCode } from '@/graphql/errors';
import { CONNECT_CLAUDE_CODE } from '@/graphql/mutations/connections';
import { track } from '@/shared/lib/analytics';
import { type ClaudeKeyProblem, claudeKeyProblem, detectClaudeKeyKind } from '../model/claude-key';

export type ConnectClaudeCodeOutcome =
  | { isConnected: true }
  | { isConnected: false; problem: ClaudeKeyProblem };

type UseConnectClaudeCodeResult = {
  connectClaudeCode: (environmentKey: string) => Promise<ConnectClaudeCodeOutcome>;
  isConnecting: boolean;
};

/** Saves the environment key (first connect and replace). The wrong kind of key never leaves the page. */
export function useConnectClaudeCode(): UseConnectClaudeCodeResult {
  const { refreshViewer } = useViewer();
  const [mutate, { loading }] = useMutation(CONNECT_CLAUDE_CODE);

  const connectClaudeCode = useCallback(
    async (environmentKey: string): Promise<ConnectClaudeCodeOutcome> => {
      const kind = detectClaudeKeyKind(environmentKey);
      if (kind === 'api-key') return { isConnected: false, problem: 'api-key' };
      if (kind === 'unknown') return { isConnected: false, problem: 'format' };
      const { data } = await mutate({
        variables: { input: { environmentKey: environmentKey.trim() } },
      });
      const payload = data?.connectClaudeCode;
      const errorCode = firstUserErrorCode(payload?.userErrors);
      if (errorCode) return { isConnected: false, problem: claudeKeyProblem(errorCode) };
      if (payload?.connection?.status === 'KEY_REJECTED')
        return { isConnected: false, problem: 'rejected' };
      track('agent_connected', { agent: 'claude_code' });
      await refreshViewer();
      return { isConnected: true };
    },
    [mutate, refreshViewer],
  );

  return { connectClaudeCode, isConnecting: loading };
}
